/**
 * L'accès au téléphone d'un technicien (lot T1) : relier un NOM de l'annuaire du planning à un COMPTE de l'espace.
 *
 * Le téléphone ne montre à un compte que les interventions du technicien qui lui est relié : sans ce lien, « Ma journée »
 * reste vide. Un compte ne se relie qu'à UN technicien (le serveur le garantit, en base aussi) ; un technicien n'a qu'un
 * compte. Ici, ce qui se décide sans écran : les choix proposés, ce qui change, les phrases.
 *
 * Le serveur reste l'autorité : un choix grisé ici est aussi refusé là-bas (409).
 */
import type { AccesPossible, Technicien } from '../api/client';

/** La valeur du choix « Aucun » dans la liste (un identifiant ne peut pas être vide). */
export const AUCUN_ACCES = '';

export interface OptionAcces {
  readonly valeur: string;
  readonly libelle: string;
  /** Vrai quand le compte est déjà relié à un AUTRE technicien : visible, mais pas choisissable. */
  readonly desactive: boolean;
}

/** « Benjamin VINCENT » ou « Aucun » : ce que la colonne « Accès au téléphone » affiche. */
export function libelleAcces(t: Pick<Technicien, 'acces'>): string {
  return t.acces ? t.acces.nom : 'Aucun';
}

/** La valeur sélectionnée dans la liste pour ce technicien. */
export function valeurAcces(t: Pick<Technicien, 'acces'>): string {
  return t.acces ? t.acces.membershipId : AUCUN_ACCES;
}

/**
 * Les choix de la liste d'un technicien : « Aucun », puis chaque compte possible. Un compte relié à un autre technicien est
 * grisé et dit à qui il est relié. Le compte actuel du technicien, s'il n'est plus parmi les comptes possibles (adhésion
 * suspendue ou révoquée depuis), reste affiché pour que la liste ne mente pas — on peut alors le délier.
 */
export function optionsAcces(
  technicien: Pick<Technicien, 'id' | 'acces' | 'archived'>,
  possibles: readonly AccesPossible[],
  techniciens: readonly Pick<Technicien, 'id' | 'name'>[],
): OptionAcces[] {
  const nomDe = new Map(techniciens.map((x) => [x.id, x.name]));
  const options: OptionAcces[] = [{ valeur: AUCUN_ACCES, libelle: 'Aucun', desactive: false }];
  for (const p of possibles) {
    const ailleurs = p.relieA !== null && p.relieA !== technicien.id;
    const actuel = technicien.acces?.membershipId === p.membershipId;
    options.push({
      valeur: p.membershipId,
      libelle: ailleurs ? `${p.nom} — déjà relié à ${nomDe.get(p.relieA ?? '') ?? 'un autre technicien'}` : p.nom,
      // Un technicien archivé peut être délié, jamais relié à un nouveau compte (règle du serveur).
      desactive: ailleurs || (technicien.archived && !actuel),
    });
  }
  const acces = technicien.acces;
  if (acces && !possibles.some((p) => p.membershipId === acces.membershipId)) {
    options.splice(1, 0, { valeur: acces.membershipId, libelle: `${acces.nom} (accès inactif)`, desactive: false });
  }
  return options;
}

/**
 * Ce qu'il faut envoyer au serveur quand on choisit `valeur` : `{ membershipId }` (identifiant pour relier, `null` pour
 * délier), ou `null` quand rien ne change — aucun envoi alors.
 */
export function changementAcces(t: Pick<Technicien, 'acces'>, valeur: string): { membershipId: string | null } | null {
  if (valeur === valeurAcces(t)) return null;
  return { membershipId: valeur === AUCUN_ACCES ? null : valeur };
}

/** La phrase dite après l'enregistrement. */
export function phraseAcces(nomTechnicien: string, nomCompte: string | null): string {
  return nomCompte === null
    ? `${nomTechnicien} n’a plus d’accès au téléphone.`
    : `${nomTechnicien} est relié au compte de ${nomCompte} : ses interventions apparaîtront sur son téléphone.`;
}

/** Le nom du compte d'une valeur choisie, d'après les comptes possibles (ou le compte actuel). */
export function nomDuCompte(valeur: string, possibles: readonly AccesPossible[], t: Pick<Technicien, 'acces'>): string | null {
  if (valeur === AUCUN_ACCES) return null;
  return possibles.find((p) => p.membershipId === valeur)?.nom ?? (t.acces?.membershipId === valeur ? t.acces.nom : null);
}

/**
 * Le refus du serveur dit en français. Un 409 vient soit d'un compte déjà relié à un autre technicien (pris entre-temps),
 * soit d'un technicien modifié depuis l'ouverture : dans les deux cas l'écran vient de relire la liste.
 */
export function messageRefusAcces(statut: number, messageServeur: string | null | undefined): string {
  const serveur = (messageServeur ?? '').trim();
  if (statut === 409) {
    const motif = serveur !== '' ? serveur : 'Ce compte est déjà relié à un autre technicien, ou ce technicien a été modifié entre-temps.';
    return `${motif} La liste vient d’être relue : vérifiez puis recommencez si besoin.`;
  }
  if (statut === 403) return 'Vous n’avez pas le droit de relier un compte à un technicien.';
  return serveur !== '' ? serveur : 'Enregistrement de l’accès impossible.';
}
